import '../App.css';
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import authgear from "../authgear";
import Header from "../components/Header";
import {
    ShiningMask,
    isWebBluetoothSupported,
    IMAGE_COUNT,
    ANIMATION_COUNT,
    DIY_SLOTS,
    MODES,
    TEXT_EFFECTS,
    hexToRgb,
    rainbow,
    renderTextColumns,
    canvasToColumnRgb,
} from "../lib/shiningMask";

const ACCENT = "#4ecdc4";

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "20px",
    marginBottom: "16px",
};

const inputStyle = {
    background: "#0d0d1a",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    color: "#ddd",
    padding: "8px 10px",
    fontSize: "13px",
    boxSizing: "border-box",
};

const labelStyle = { fontSize: "12px", color: "#888", display: "block", marginBottom: "4px" };

const sectionLabel = {
    fontSize: "11px",
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "12px",
};

const button = (primary, disabled) => ({
    padding: "8px 16px",
    background: primary ? ACCENT : "#0d0d1a",
    color: primary ? "#0d0d1a" : "#ddd",
    border: primary ? "none" : "1px solid #2a2a4a",
    borderRadius: "6px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    fontWeight: primary ? "bold" : "normal",
    fontSize: "13px",
});

const tile = (active) => ({
    width: "40px",
    height: "32px",
    background: active ? ACCENT : "#0d0d1a",
    color: active ? "#0d0d1a" : "#bbb",
    border: "1px solid #2a2a4a",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "12px",
});

const row = { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" };

const NumberGrid = ({ count, active, onPick, disabled }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {Array.from({ length: count }, (_, i) => i + 1).map(n => (
            <button key={n} disabled={disabled} style={tile(active === n)} onClick={() => onPick(n)}>
                {n}
            </button>
        ))}
    </div>
);

const Mask = () => {
    const navigate = useNavigate();
    const [connected, setConnected] = useState(false);
    const [deviceName, setDeviceName] = useState(null);
    const [busy, setBusy] = useState(false);
    const [log, setLog] = useState([]);
    const [active, setActive] = useState(null); // "i:3" / "a:7"

    const [brightness, setBrightness] = useState(100);
    const [speed, setSpeed] = useState(50);
    const [mode, setMode] = useState(3);

    const [text, setText] = useState("HELLO");
    const [textColor, setTextColor] = useState("#4ecdc4");
    const [useRainbow, setUseRainbow] = useState(false);
    const [fontSize, setFontSize] = useState(14);
    const [effect, setEffect] = useState(-1);

    const [picture, setPicture] = useState(null);
    const [picWidth, setPicWidth] = useState(46);
    const [picHeight, setPicHeight] = useState(58);
    const [picSlot, setPicSlot] = useState(DIY_SLOTS);
    const [picFit, setPicFit] = useState("cover");

    const [rawName, setRawName] = useState("LIGHT");
    const [rawArgs, setRawArgs] = useState("100");

    const textPreviewRef = useRef(null);
    const picCanvasRef = useRef(null);

    const addLog = useCallback((msg) => {
        const t = new Date().toLocaleTimeString([], { hour12: false });
        setLog(l => [`${t} ${msg}`, ...l].slice(0, 200));
    }, []);

    const mask = useMemo(() => new ShiningMask({
        onLog: addLog,
        onConnectionChange: (on, name) => { setConnected(on); setDeviceName(name); },
    }), [addLog]);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") navigate("/login");
    }, [navigate]);

    useEffect(() => () => mask.disconnect(), [mask]);

    const run = async (fn) => {
        try {
            await fn();
        } catch (e) {
            addLog(`error: ${e.message}`);
        }
    };

    const runBusy = async (fn) => {
        setBusy(true);
        await run(fn);
        setBusy(false);
    };

    const connect = (showAll) => run(() => mask.connect({ showAll }));

    // ---- text ----
    const textColumns = useMemo(() => renderTextColumns(text, { fontSize }), [text, fontSize]);
    const columnColors = useMemo(
        () => textColumns.map((_, x) => (useRainbow ? rainbow(x) : hexToRgb(textColor))),
        [textColumns, useRainbow, textColor],
    );

    useEffect(() => {
        const cv = textPreviewRef.current;
        if (!cv) return;
        const S = 4;
        cv.width = textColumns.length * S;
        cv.height = 16 * S;
        const ctx = cv.getContext("2d");
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, cv.width, cv.height);
        textColumns.forEach((col, x) => col.forEach((on, y) => {
            if (!on) return;
            ctx.fillStyle = `rgb(${columnColors[x].join(",")})`;
            ctx.fillRect(x * S, y * S, S - 1, S - 1);
        }));
    }, [textColumns, columnColors]);

    const sendText = () => runBusy(() => mask.uploadText(textColumns, columnColors, {
        mode,
        speed,
        effect,
        textColor: useRainbow ? null : hexToRgb(textColor),
    }));

    // ---- picture ----
    useEffect(() => {
        const cv = picCanvasRef.current;
        if (!cv) return;
        cv.width = picWidth;
        cv.height = picHeight;
        const ctx = cv.getContext("2d", { willReadFrequently: true });
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, picWidth, picHeight);
        if (!picture) return;
        const iw = picture.naturalWidth;
        const ih = picture.naturalHeight;
        const s = picFit === "cover"
            ? Math.max(picWidth / iw, picHeight / ih)
            : Math.min(picWidth / iw, picHeight / ih);
        const w = iw * s;
        const h = ih * s;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(picture, (picWidth - w) / 2, (picHeight - h) / 2, w, h);
    }, [picture, picWidth, picHeight, picFit]);

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const img = new Image();
        img.onload = () => { setPicture(img); URL.revokeObjectURL(img.src); };
        img.onerror = () => addLog("could not read that picture");
        img.src = URL.createObjectURL(f);
    };

    const sendPicture = () => runBusy(() => mask.uploadPicture(canvasToColumnRgb(picCanvasRef.current), +picSlot));

    // ---- raw ----
    const sendRaw = () => {
        const args = rawArgs.split(/[\s,]+/).filter(Boolean).map(Number);
        if (args.some(n => !Number.isInteger(n) || n < 0 || n > 255)) {
            addLog("raw args must be numbers 0-255, separated by spaces or commas");
            return;
        }
        run(() => mask.command(rawName.trim().toUpperCase(), args));
    };

    const off = !connected;

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "820px", margin: "0 auto" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <div>
                        <h2 style={{ color: ACCENT, margin: 0 }}>LED Mask</h2>
                        <div style={{ fontSize: "12px", color: "#555", marginTop: "4px" }}>
                            <span style={{
                                display: "inline-block", width: "8px", height: "8px", borderRadius: "50%",
                                background: connected ? ACCENT : "#553", marginRight: "6px",
                            }} />
                            {connected ? `Connected to ${deviceName || "mask"}` : "Not connected"}
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        {connected ? (
                            <button style={button(false)} onClick={() => mask.disconnect()}>Disconnect</button>
                        ) : (
                            <>
                                <button style={button(true, !isWebBluetoothSupported())} disabled={!isWebBluetoothSupported()} onClick={() => connect(false)}>
                                    Connect
                                </button>
                                <button style={button(false, !isWebBluetoothSupported())} disabled={!isWebBluetoothSupported()} onClick={() => connect(true)} title="Show every Bluetooth device, in case the mask doesn't advertise as MASK-…">
                                    Show all devices
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {!isWebBluetoothSupported() && (
                    <div style={{ ...card, borderColor: "#6a4a2a", color: "#dba" }}>
                        This browser has no Web Bluetooth. Open this page in Chrome or Edge on Windows, Mac or Android (iPhone browsers don't support it).
                    </div>
                )}
                {!connected && isWebBluetoothSupported() && (
                    <div style={{ ...card, fontSize: "13px", color: "#888" }}>
                        Turn the mask on and close the Shining Mask phone app (the mask takes one connection at a time), then click Connect and pick the device named MASK-….
                    </div>
                )}

                <div style={card}>
                    <div style={sectionLabel}>Display</div>
                    <div style={row}>
                        <div>
                            <label style={labelStyle}>Brightness: {brightness}</label>
                            <input type="range" min="0" max="255" value={brightness} disabled={off}
                                onChange={e => setBrightness(+e.target.value)}
                                onPointerUp={() => run(() => mask.setBrightness(brightness))}
                                onKeyUp={() => run(() => mask.setBrightness(brightness))} />
                        </div>
                        <div>
                            <label style={labelStyle}>Speed: {speed}</label>
                            <input type="range" min="0" max="255" value={speed} disabled={off}
                                onChange={e => setSpeed(+e.target.value)}
                                onPointerUp={() => run(() => mask.setSpeed(speed))}
                                onKeyUp={() => run(() => mask.setSpeed(speed))} />
                        </div>
                        <div>
                            <label style={labelStyle}>Text mode</label>
                            <select style={inputStyle} value={mode} disabled={off}
                                onChange={e => { setMode(+e.target.value); run(() => mask.setMode(+e.target.value)); }}>
                                {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                <div style={card}>
                    <div style={sectionLabel}>Animations</div>
                    <NumberGrid count={ANIMATION_COUNT} disabled={off}
                        active={active?.startsWith("a:") ? +active.slice(2) : null}
                        onPick={n => { setActive(`a:${n}`); run(() => mask.showAnimation(n)); }} />
                </div>

                <div style={card}>
                    <div style={sectionLabel}>Faces / images</div>
                    <NumberGrid count={IMAGE_COUNT} disabled={off}
                        active={active?.startsWith("i:") ? +active.slice(2) : null}
                        onPick={n => { setActive(`i:${n}`); run(() => mask.showImage(n)); }} />
                </div>

                <div style={card}>
                    <div style={sectionLabel}>Scrolling text</div>
                    <div style={row}>
                        <div style={{ flex: "1 1 220px" }}>
                            <label style={labelStyle}>Text</label>
                            <input style={{ ...inputStyle, width: "100%" }} value={text} maxLength={80}
                                onChange={e => setText(e.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>Color</label>
                            <input type="color" value={textColor} disabled={useRainbow}
                                onChange={e => setTextColor(e.target.value)} />
                        </div>
                        <label style={{ fontSize: "13px", color: "#bbb", display: "flex", gap: "6px", alignItems: "center" }}>
                            <input type="checkbox" checked={useRainbow} onChange={e => setUseRainbow(e.target.checked)} />
                            Rainbow
                        </label>
                        <div>
                            <label style={labelStyle}>Font size</label>
                            <select style={inputStyle} value={fontSize} onChange={e => setFontSize(+e.target.value)}>
                                {[10, 12, 13, 14, 15, 16].map(s => <option key={s} value={s}>{s}px</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Effect</label>
                            <select style={inputStyle} value={effect} onChange={e => setEffect(+e.target.value)}>
                                {TEXT_EFFECTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ margin: "14px 0", overflowX: "auto", background: "#000", borderRadius: "6px", padding: "6px" }}>
                        <canvas ref={textPreviewRef} style={{ display: "block" }} />
                    </div>
                    <button style={button(true, off || busy)} disabled={off || busy} onClick={sendText}>
                        {busy ? "Sending…" : "Send text to mask"}
                    </button>
                </div>

                <div style={card}>
                    <div style={sectionLabel}>Picture (experimental)</div>
                    <div style={{ fontSize: "12px", color: "#777", marginBottom: "12px" }}>
                        Uploads into a custom (DIY) slot and plays it. 46×58 is the size one owner confirmed;
                        if your mask's picture comes out scrambled, its panel is a different size.
                        Slot {DIY_SLOTS} is the default so the app's own pictures aren't overwritten.
                    </div>
                    <div style={row}>
                        <div>
                            <label style={labelStyle}>Image file</label>
                            <input type="file" accept="image/*" onChange={onPickFile} style={{ fontSize: "12px" }} />
                        </div>
                        <div>
                            <label style={labelStyle}>Fit</label>
                            <select style={inputStyle} value={picFit} onChange={e => setPicFit(e.target.value)}>
                                <option value="cover">Fill</option>
                                <option value="contain">Fit whole picture</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Width × height</label>
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                <input style={{ ...inputStyle, width: "60px" }} type="number" min="8" max="128" value={picWidth}
                                    onChange={e => setPicWidth(Math.max(8, Math.min(128, +e.target.value || 46)))} />
                                ×
                                <input style={{ ...inputStyle, width: "60px" }} type="number" min="8" max="128" value={picHeight}
                                    onChange={e => setPicHeight(Math.max(8, Math.min(128, +e.target.value || 58)))} />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Slot</label>
                            <select style={inputStyle} value={picSlot} onChange={e => setPicSlot(+e.target.value)}>
                                {Array.from({ length: DIY_SLOTS }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", marginTop: "14px", flexWrap: "wrap" }}>
                        <canvas ref={picCanvasRef} style={{
                            width: `${picWidth * 4}px`, height: `${picHeight * 4}px`, maxWidth: "100%",
                            imageRendering: "pixelated", background: "#000", borderRadius: "6px",
                        }} />
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <button style={button(true, off || busy || !picture)} disabled={off || busy || !picture} onClick={sendPicture}>
                                {busy ? "Uploading…" : "Upload picture"}
                            </button>
                            <button style={button(false, off)} disabled={off} onClick={() => run(() => mask.playDiy(+picSlot))}>
                                Play slot {picSlot}
                            </button>
                        </div>
                    </div>
                </div>

                <div style={card}>
                    <div style={sectionLabel}>Raw command</div>
                    <div style={{ fontSize: "12px", color: "#777", marginBottom: "12px" }}>
                        For experimenting: sends any command name with byte arguments, e.g. LIGHT 100, IMAG 0, FC 1 255 0 0.
                    </div>
                    <div style={row}>
                        <input style={{ ...inputStyle, width: "110px" }} value={rawName} onChange={e => setRawName(e.target.value)} />
                        <input style={{ ...inputStyle, width: "180px" }} value={rawArgs} onChange={e => setRawArgs(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && !off && sendRaw()} />
                        <button style={button(false, off)} disabled={off} onClick={sendRaw}>Send</button>
                    </div>
                </div>

                <div style={card}>
                    <div style={{ ...sectionLabel, display: "flex", justifyContent: "space-between" }}>
                        <span>Log</span>
                        <button style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: "11px" }} onClick={() => setLog([])}>
                            Clear
                        </button>
                    </div>
                    <pre style={{ margin: 0, fontSize: "11px", color: "#8a8", maxHeight: "220px", overflowY: "auto", whiteSpace: "pre-wrap" }}>
                        {log.length ? log.join("\n") : "Nothing yet."}
                    </pre>
                </div>
            </div>
        </div>
    );
};

export default Mask;
